import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, MailCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { decodeJwt } from '@/lib/api-client';
import { useAuthActions } from '@/stores';

import { magicLinkApi } from '../services/auth.service';
import { mapLoginResponseToAuthSession } from '../utils/auth-session.mapper';

type MagicLinkVerifyStatus = 'verifying' | 'success' | 'missing-token' | 'invalid';

function getErrorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message;
  }

  return 'This sign-in link is invalid or has expired.';
}

export function MagicLinkVerifyPage() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const search = routerState.location.search as { token?: string };
  const token = search.token?.trim() ?? '';
  const { login: setAuthState } = useAuthActions();
  const submittedToken = useRef<string | null>(null);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<MagicLinkVerifyStatus>(
    token ? 'verifying' : 'missing-token',
  );
  const [errorMessage, setErrorMessage] = useState('This sign-in link is invalid or has expired.');

  useEffect(() => {
    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (submittedToken.current === token) {
      return;
    }

    submittedToken.current = token;

    void magicLinkApi
      .verify({ token })
      .then((response) => {
        const { user, tenant } = mapLoginResponseToAuthSession(
          response,
          decodeJwt(response.accessToken),
        );
        setAuthState(user, tenant);
        setStatus('success');
        toast.success('Signed in with magic link', {
          description: `Welcome back, ${user.fullName}.`,
        });
        redirectTimer.current = setTimeout(() => {
          void navigate({ to: '/dashboard' });
        }, 800);
      })
      .catch((error: unknown) => {
        setErrorMessage(getErrorMessage(error));
        setStatus('invalid');
        toast.error('Magic link could not be used', {
          description: 'Request a fresh sign-in link and try again.',
        });
      });
  }, [navigate, setAuthState, token]);

  if (status === 'missing-token') {
    return (
      <MagicLinkShell
        icon={<AlertCircle className="h-8 w-8 text-white" />}
        iconClassName="bg-red-600"
        title="Sign-in link is missing"
        description="The link does not include the secure token required to sign you in."
      >
        <p className="text-center text-sm leading-6 text-muted-foreground">
          Open the latest email from UniTree or request a new magic link from the sign-in page.
        </p>
        <AuthLinkActions />
      </MagicLinkShell>
    );
  }

  if (status === 'invalid') {
    return (
      <MagicLinkShell
        icon={<AlertCircle className="h-8 w-8 text-white" />}
        iconClassName="bg-red-600"
        title="This sign-in link cannot be used"
        description={errorMessage}
      >
        <p className="text-center text-sm leading-6 text-muted-foreground">
          Magic links are single-use and expire for security. Request a fresh link to continue.
        </p>
        <AuthLinkActions />
      </MagicLinkShell>
    );
  }

  if (status === 'success') {
    return (
      <MagicLinkShell
        icon={<CheckCircle2 className="h-8 w-8 text-white" />}
        iconClassName="bg-emerald-700"
        title="You're signed in"
        description="Your magic link was verified."
      >
        <p className="text-center text-sm leading-6 text-muted-foreground">
          Redirecting to your dashboard now.
        </p>
        <Link to="/dashboard" className="block">
          <Button className="w-full" size="lg">
            Continue to dashboard
          </Button>
        </Link>
      </MagicLinkShell>
    );
  }

  return (
    <MagicLinkShell
      icon={<Loader2 className="h-8 w-8 animate-spin text-white" />}
      iconClassName="bg-slate-950"
      title="Verifying your sign-in link"
      description="This should only take a moment."
    >
      <div
        className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
        role="status"
      >
        <MailCheck className="h-4 w-4" />
        Checking the secure token
      </div>
    </MagicLinkShell>
  );
}

function MagicLinkShell({
  children,
  description,
  icon,
  iconClassName,
  title,
}: {
  children: ReactNode;
  description: string;
  icon: ReactNode;
  iconClassName: string;
  title: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 dark:from-gray-950 dark:via-gray-900 dark:to-slate-900">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mb-4 flex justify-center">
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg ${iconClassName}`}
            >
              {icon}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold tracking-normal">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </div>
  );
}

function AuthLinkActions() {
  return (
    <div className="space-y-3">
      <Link to="/login" className="block">
        <Button className="w-full" size="lg">
          Request a new sign-in link
        </Button>
      </Link>
      <Link to="/login" className="block">
        <Button variant="ghost" className="w-full">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to sign in
        </Button>
      </Link>
    </div>
  );
}
